import { useState, useEffect, useRef, useId } from 'react';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import { Label } from '@eliadmin/core/src/components/ui/label';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { Input } from '@eliadmin/core/src/components/ui/input';

export const CheckboxesFilter = ({
  title,
  options,
  selectedOptions,
  onChange,
  className,
  search,
  sortBySelected,
  searchPlaceholder,
}: {
  title: string;
  options: { value: string; label: string }[];
  selectedOptions: string[];
  onChange: (selectedOptions: string[]) => void;
  className?: string;
  search?: boolean;
  sortBySelected?: boolean;
  searchPlaceholder?: string;
}) => {
  const { t } = useTranslations();
  const id = useId();
  const [visibleCount, setVisibleCount] = useState(7);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortedOptions, setSortedOptions] = useState(options);
  const hasSortedOnMount = useRef(false);

  useEffect(() => {
    if (sortBySelected && !hasSortedOnMount.current) {
      // Sort options so that selected options appear first
      const sorted = [...options].sort((a, b) => {
        const aSelected = selectedOptions.includes(a.value);
        const bSelected = selectedOptions.includes(b.value);
        if (aSelected && !bSelected) return -1;
        if (!aSelected && bSelected) return 1;
        return 0;
      });
      setSortedOptions(sorted);
      hasSortedOnMount.current = true;
    }
  }, [options, sortBySelected]);

  const handleShowMore = () => {
    setVisibleCount(prevCount => prevCount + 8);
  };

  // Filter options based on search query
  const filteredOptions = searchQuery
    ? sortedOptions.filter(option => option.label.toLowerCase().includes(searchQuery.toLowerCase()))
    : sortedOptions;

  return (
    <div className={cn('flex flex-col gap-1 space-y-2', className)}>
      <div className="w-full text-start text-lg font-semibold text-[#63626F]">{title}</div>
      {search && (
        <Input
          aria-label={searchPlaceholder || `${t('text871')}: ${title}`}
          placeholder={searchPlaceholder || t('text871')}
          className={{
            main: 'border border-border bg-white',
          }}
          onChange={ev => {
            setSearchQuery(ev.target.value);
            setVisibleCount(7);
          }}
        />
      )}
      {filteredOptions.slice(0, visibleCount).map(option => (
        <div key={option.value} className="flex items-center gap-2">
          <Checkbox
            id={`${id}-${option.value}`}
            title={option.label}
            checked={selectedOptions.includes(option.value)}
            onCheckedChange={checked =>
              onChange(
                checked
                  ? [...selectedOptions, option.value]
                  : selectedOptions.filter(selectedOption => selectedOption !== option.value),
              )
            }
          />
          <Label htmlFor={`${id}-${option.value}`} className={cn('pt-[2px]', selectedOptions.includes(option.value) && 'font-bold')}>
            {option.label}
          </Label>
        </div>
      ))}
      {visibleCount < filteredOptions.length && (
        <button
          onClick={handleShowMore}
          className="mt-2 text-start font-semibold text-primary hover:underline"
        >
          {t('text1715')}
        </button>
      )}
    </div>
  );
};
